import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { ZodType } from 'zod';

import type { FieldConfig } from '@/types/forms';

import { createEnquirySchema } from '@/lib/forms/schemas/enquirySchema';
import {
  contactFormDefaultValues,
  contactFormFields,
  contactFormSchema,
} from '@/features/contactUs/forms/contactSchema';
import {
  careersFormDefaultValues,
  careersFormFields,
  careersFormSchema,
} from '@/features/careers/forms/careersFormSchema';
import {
  defaultValues as feedbackDefaultValues,
  fields as feedbackFields,
  schema as feedbackSchema,
} from '@/features/feedback/forms/feedbackSchema';
import {
  doctorEnquiryDefaultValues,
  doctorEnquiryFields,
} from '@/features/doctors/forms/doctorEnquirySchema';
import {
  serviceEnquiryDefaultValues,
  serviceEnquiryFields,
} from '@/features/enquiries/forms/serviceEnquirySchema';
import {
  bloodTestEnquiryDefaultValues,
  bloodTestEnquiryFields,
} from '@/features/blood-test/forms/bloodTestEnquirySchema';
import {
  boneScanEnquiryDefaultValues,
  boneScanEnquiryFields,
} from '@/features/boneScan/forms/boneScanEnquirySchema';
import {
  dentalEnquiryDefaultValues,
  dentalEnquiryFields,
} from '@/features/dentalServices/forms/dentalEnquirySchema';
import {
  exroidEnquirySchema,
  exroidEnquiryDefaultValues,
  exroidEnquiryFields,
} from '@/features/exroid/forms/exroidEnquirySchema';
import {
  physiotherapyEnquiryDefaultValues,
  physiotherapyEnquiryFields,
} from '@/features/physiotherapy/forms/physiotherapyEnquirySchema';
import {
  trtEnquiryDefaultValues,
  trtEnquiryFields,
} from '@/features/trt/forms/trtEnquirySchema';
import {
  maleUltrasoundEnquiryDefaultValues,
  maleUltrasoundEnquiryFields,
} from '@/features/privateUltrasoundScansBattersea/forms/MaleUltrasoundEnquiry';
import {
  femaleUltrasoundEnquiryDefaultValues,
  femaleUltrasoundEnquiryFields,
} from '@/features/femaleUltrasound/forms/FemaleUltrasoundEnquiry';
import {
  privateUltrasoundScansEnquiryDefaultValues,
  privateUltrasoundScansEnquiryFields,
} from '@/features/privateUltrasoundScans/forms/PrivateUltrasoundScansEnquiry';
import {
  gpAccessEnquiryDefaultValues,
  gpAccessEnquiryFields,
} from '@/features/gpAccess/forms/GPAccessEnquiry';
import {
  urgentCareEnquiryDefaultValues,
  urgentCareEnquiryFields,
} from '@/features/urgentCare/forms/UrgentCareEnquiry';
import {
  dieteticsEnquiryDefaultValues,
  dieteticsEnquiryFields,
} from '@/features/dietetics/forms/DieteticsEnquiry';
import {
  investorEnquiryDefaultValues,
  investorEnquiryFields,
  investorEnquirySchema,
} from '@/features/investorRelations/forms/investorEnquirySchema';
import {
  schema as submitComplaintSchema,
  defaultValues as submitComplaintDefaultValues,
  fields as submitComplaintFields,
} from '@/features/submitComplaint/forms/submitComplaintSchema';

export type FormDefinition<T extends FieldValues = FieldValues> = {
  formId: string;
  schema: ZodType<T>;
  fields: FieldConfig[];
  defaultValues: DefaultValues<T>;
};

export const formDefinitions: Record<string, FormDefinition> = {
  contact: {
    formId: 'contact',
    schema: contactFormSchema,
    fields: contactFormFields,
    defaultValues: contactFormDefaultValues,
  },
  careers: {
    formId: 'careers',
    schema: careersFormSchema,
    fields: careersFormFields,
    defaultValues: careersFormDefaultValues,
  },
  feedback: {
    formId: 'feedback',
    schema: feedbackSchema,
    fields: feedbackFields,
    defaultValues: feedbackDefaultValues,
  },
  doctorEnquiry: {
    formId: 'doctorEnquiry',
    schema: createEnquirySchema({
      messageMax: 1000,
      messageMin: 10,
      fullNameMin: 2,
      phoneMin: 7,
    }),
    fields: doctorEnquiryFields,
    defaultValues: doctorEnquiryDefaultValues,
  },
  serviceEnquiry: {
    formId: 'serviceEnquiry',
    schema: createEnquirySchema({
      messageMin: 10,
      fullNameMin: 2,
      phoneMin: 7,
    }),
    fields: serviceEnquiryFields,
    defaultValues: serviceEnquiryDefaultValues,
  },
  bloodTestEnquiry: {
    formId: 'bloodTestEnquiry',
    schema: createEnquirySchema({ includePreferredContact: false }),
    fields: bloodTestEnquiryFields,
    defaultValues: bloodTestEnquiryDefaultValues,
  },
  boneScanEnquiry: {
    formId: 'boneScanEnquiry',
    schema: createEnquirySchema({
      includePreferredContact: false,
      useUkPhoneRegex: true,
    }),
    fields: boneScanEnquiryFields,
    defaultValues: boneScanEnquiryDefaultValues,
  },
  dentalEnquiry: {
    formId: 'dentalEnquiry',
    schema: createEnquirySchema({}),
    fields: dentalEnquiryFields,
    defaultValues: dentalEnquiryDefaultValues,
  },
  exroidEnquiry: {
    formId: 'exroidEnquiry',
    schema: exroidEnquirySchema,
    fields: exroidEnquiryFields,
    defaultValues: exroidEnquiryDefaultValues,
  },
  physiotherapyEnquiry: {
    formId: 'physiotherapyEnquiry',
    schema: createEnquirySchema({}),
    fields: physiotherapyEnquiryFields,
    defaultValues: physiotherapyEnquiryDefaultValues,
  },
  trtEnquiry: {
    formId: 'trtEnquiry',
    schema: createEnquirySchema({}),
    fields: trtEnquiryFields,
    defaultValues: trtEnquiryDefaultValues,
  },
  MaleUltrasoundEnquiry: {
    formId: 'MaleUltrasoundEnquiry',
    schema: createEnquirySchema({ includePreferredContact: false }),
    fields: maleUltrasoundEnquiryFields,
    defaultValues: maleUltrasoundEnquiryDefaultValues,
  },
  FemaleUltrasoundEnquiry: {
    formId: 'FemaleUltrasoundEnquiry',
    schema: createEnquirySchema({ includePreferredContact: false }),
    fields: femaleUltrasoundEnquiryFields,
    defaultValues: femaleUltrasoundEnquiryDefaultValues,
  },
  PrivateUltrasoundScansEnquiry: {
    formId: 'PrivateUltrasoundScansEnquiry',
    schema: createEnquirySchema({ includePreferredContact: false }),
    fields: privateUltrasoundScansEnquiryFields,
    defaultValues: privateUltrasoundScansEnquiryDefaultValues,
  },
  GPAccessEnquiry: {
    formId: 'GPAccessEnquiry',
    schema: createEnquirySchema({ includePreferredContact: false }),
    fields: gpAccessEnquiryFields,
    defaultValues: gpAccessEnquiryDefaultValues,
  },
  UrgentCareEnquiry: {
    formId: 'UrgentCareEnquiry',
    schema: createEnquirySchema({ includePreferredContact: false }),
    fields: urgentCareEnquiryFields,
    defaultValues: urgentCareEnquiryDefaultValues,
  },
  DieteticsEnquiry: {
    formId: 'DieteticsEnquiry',
    schema: createEnquirySchema({
      includePreferredContact: false,
      useUkPhoneRegex: true,
    }),
    fields: dieteticsEnquiryFields,
    defaultValues: dieteticsEnquiryDefaultValues,
  },
  investorEnquiry: {
    formId: 'investorEnquiry',
    schema: investorEnquirySchema,
    fields: investorEnquiryFields,
    defaultValues: investorEnquiryDefaultValues,
  },
  submitComplaint: {
    formId: 'submitComplaint',
    schema: submitComplaintSchema,
    fields: submitComplaintFields,
    defaultValues: submitComplaintDefaultValues,
  },
};
export function getFormDefinition(formId: string): FormDefinition | undefined {
  return formDefinitions[formId];
}
